import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { objectiveService } from '../../services/objectiveService';

/**
 * Hook for fetching a single objective by ID with real-time updates
 * @param objectiveId - The ID of the objective to fetch
 * @returns Query result with objective data
 */
export function useObjectiveQuery(objectiveId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: ['objective', objectiveId],
    queryFn: async () => {
      if (!objectiveId) {
        throw new Error('Objective ID is required');
      }
      return await objectiveService.getById(objectiveId);
    },
    enabled: !!objectiveId,
    staleTime: Infinity, // Data stays fresh because we use realtime updates
  });

  // Set up real-time listener
  useEffect(() => {
    if (!objectiveId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'objectives', objectiveId),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const objective = {
            id: snapshot.id,
            ...data,
          };

          // Update query cache with new data
          queryClient.setQueryData(['objective', objectiveId], objective);
        }
      },
      (error) => {
        console.error('Error listening to objective updates:', error);
      }
    );

    return () => unsubscribe();
  }, [objectiveId, queryClient]);

  return queryResult;
}
